// src/components/templates/TemplateCover.jsx
//
// A template's book cover: the prototype's illustrated covers, served as static
// SVGs from `public/template-covers/<template id>.svg`.
//
// **Why static files, and why inlined.** The covers are artwork with hundreds of
// literal colours — a sunset, a red curtain, a kraft folder. They are not the
// app's chrome, so they are not drawn from `theme.js` (recolouring them to two
// brand colours would make twenty covers look alike), and keeping them out of
// `src/` keeps them out of the token linter, which is for chrome. They are
// fetched and placed inline rather than used as an <img> because an SVG in an
// <img> cannot use the page's web fonts, and the titles are set in Playfair,
// Courier Prime and Bangers.
//
// Every id inside a file is prefixed `__U__`. Each instance swaps that for its
// own prefix, so the same cover twice on one page (the card and the detail
// view's carousel) never resolves a gradient from the other copy.
//
// A template without a file gets a plain cover with its name, so a template
// added to the catalogue before its artwork still has a book to click.
import React, { useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { alpha } from '@mui/material/styles';

import { SP, FONT_DISPLAY } from './templateTokens';

const cache = new Map();

function loadCover(id) {
  if (!cache.has(id)) {
    cache.set(id, fetch(`/template-covers/${encodeURIComponent(id)}.svg`)
      .then((res) => (res.ok ? res.text() : ''))
      // A dev server answers an unknown path with index.html and a 200.
      .then((text) => (text.trim().startsWith('<svg') ? text : ''))
      .catch(() => ''));
  }
  const pending = cache.get(id);
  // Only artwork is kept; a miss is asked again next time rather than for good.
  pending.then((text) => { if (!text) cache.delete(id); });
  return pending;
}

let instances = 0;

export const bookSx = {
  position: 'relative',
  borderRadius: '2px 5px 5px 2px',
  overflow: 'hidden',
  boxShadow: [
    `0 1px 1px ${alpha(SP.black, 0.15)}`,
    `0 10px 22px -6px ${alpha(SP.purpleDeep, 0.45)}`,
    `0 3px 6px ${alpha(SP.black, 0.12)}`,
  ].join(','),
  '& svg': { display: 'block', width: '100%', height: '100%' },
};

export default function TemplateCover({ id, name, sx, ...rest }) {
  const [svg, setSvg] = useState(null);
  const prefix = useMemo(() => { instances += 1; return `tc${instances}_`; }, []);

  useEffect(() => {
    let cancelled = false;
    loadCover(id).then((text) => { if (!cancelled) setSvg(text); });
    return () => { cancelled = true; };
  }, [id]);

  const html = useMemo(() => (svg ? svg.split('__U__').join(prefix) : ''), [svg, prefix]);

  return (
    <Box sx={{ ...bookSx, ...sx }} {...rest}>
      {html ? (
        <Box sx={{ width: '100%', height: '100%' }} dangerouslySetInnerHTML={{ __html: html }} />
      ) : (
        <Box
          role="img"
          aria-label={`${name} cover`}
          sx={{
            width: '100%',
            height: '100%',
            background: SP.purpleDeep,
            color: SP.white,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            p: '12%',
            fontFamily: FONT_DISPLAY,
            fontWeight: 600,
            fontSize: 'clamp(11px, 1.1em, 22px)',
            lineHeight: 1.2,
          }}
        >
          {svg === null ? '' : name}
        </Box>
      )}
    </Box>
  );
}
